/**
 * Extract a human-readable error message from an axios error.
 * Handles:
 *   - Pydantic v2 validation arrays: [{loc, msg, type}]
 *   - FastAPI HTTPException strings: { detail: "..." }
 *   - Network / no-response: falls back to provided default
 */
export function apiErrorMessage(err, fallback = "Something went wrong") {
  // Network / no-response (likely offline)
  if (err && !err.response) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "You appear to be offline. Reconnect and try again.";
    }
    return err.message || fallback;
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const field = Array.isArray(d.loc) ? d.loc.filter((s) => s !== "body").join(".") : "";
      const msg = d.msg || JSON.stringify(d);
      return field ? `${field}: ${msg}` : msg;
    }).join(", ");
  }
  if (typeof detail === "string") return detail;
  // R138bk · Structured HTTPException detail objects (e.g.
  //   {code:"task_not_in_progress", message:"…", suggested_action:…})
  // — prefer the human `message` field so toasts render cleanly.
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  // Feb 2026 · Studio-booking availability conflict shape:
  //   {msg: "Not available", conflicts: {space:[], equipment:{}, off_hours:[{date, reason, open_hour?, close_hour?}]}}
  // Turn it into a human sentence so admins don't see a raw JSON blob.
  if (detail && typeof detail === "object" && detail.msg && detail.conflicts) {
    return formatAvailabilityConflicts(detail.conflicts) || detail.msg;
  }
  try { return JSON.stringify(detail); } catch { return fallback; }
}

/** Feb 2026 · Human-readable message for a studio-booking conflicts blob.
 *  Oct 2026 · Softened the copy — customers don't need to know
 *  whether we're closed, in a blackout or outside hours; the single
 *  generic "Selected slot is not available" is friendlier and keeps
 *  our operating patterns private. The underlying reason codes are
 *  still returned by the backend so admin tooling can inspect them. */
export function formatAvailabilityConflicts(c) {
  if (!c || typeof c !== "object") return "";
  const parts = [];
  const oh = c.off_hours || [];
  const seenDates = new Set();
  for (const o of oh) {
    const d = o.date || "";
    if (seenDates.has(d)) continue;
    seenDates.add(d);
    parts.push(d ? `Selected slot is not available on ${d}` : "Selected slot is not available");
  }
  if ((c.space || []).length) parts.push("Selected slot is not available");
  const eq = c.equipment || {};
  const eqKeys = Object.keys(eq);
  if (eqKeys.length) parts.push(`${eqKeys.length} equipment item(s) reserved`);
  // De-duplicate so three off-hour clauses + a space clause don't
  // produce "unavailable · unavailable · unavailable".
  const uniq = Array.from(new Set(parts));
  return uniq.length ? uniq.join(" · ") : "";
}
